/**
 * NotificationBell: the Learning Center bell in the module AppBar's right
 * slot (plan docs/plans/lms-plan.md 9.4, 9.2; R4; WP-48). The IXVerify bell's
 * shape (frontend/src/ixverify/components/shared/NotificationBell.tsx: an
 * on-gradient icon button with a red count badge and a dropdown panel),
 * rebuilt over the module's own calls.
 *
 * - The badge is the summary's unread_notifications (GET /me/summary), which
 *   LmsProvider polls every 60 s while the page is visible (D14); this
 *   component is the summary's consumer in the shell (useLmsSummary), so the
 *   poll runs exactly while the module shell is mounted.
 * - Opening the panel asks for the summary again (a fresh count) and mounts
 *   WP-41's NotificationInbox (18.28: props pageSize, reloadKey,
 *   onUnreadChange) as the list: unread first, newest first, "Show more",
 *   "Mark all read", and a row opens its module path after marking it read.
 *   The inbox's unread count flows back into the summary (noteUnread), so the
 *   badge follows a read at once instead of at the next poll. The report the
 *   inbox makes while its first page is still loading (a zero from its empty
 *   state) is ignored, so the badge never blinks to nothing on open.
 * - The panel closes on a route change (a row opened), an outside press or
 *   Escape. Nothing here uses window.alert or confirm (9.4).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useLmsSummary } from '../LmsProvider';
import { NotificationInbox } from './NotificationInbox';

/** Rows per page in the panel (Appendix A caps a page at 200). */
export const BELL_PAGE_SIZE = 10;

/** The badge's text: the count, or "9+" past nine (the IXVerify bell's rule). */
export function bellBadgeText(unread: number): string {
  return unread > 9 ? '9+' : String(unread);
}

export function NotificationBell() {
  const { summary, refresh, noteUnread } = useLmsSummary();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const unread = Math.max(0, Number(summary?.unread_notifications) || 0);

  // A row opened its module path (or the person navigated): the panel closes.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close on an outside press or Escape (the IXVerify bell's behaviour).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    // Opening asks for a fresh count at once rather than waiting for the next tick.
    if (!open) refresh();
    setOpen(!open);
  };

  // The inbox tells its unread total whenever it changes. While its first page
  // is loading it reports the zero of its empty state; that one is not news.
  const onUnreadChange = useCallback(
    (count: number) => {
      const panel = rootRef.current;
      if (!panel || panel.querySelector('[data-testid="lms-inbox-loading"]')) return;
      noteUnread(count);
    },
    [noteUnread]
  );

  return (
    <div ref={rootRef} className="relative" data-testid="lms-bell">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid="lms-bell-button"
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 sm:h-9 sm:min-h-0 sm:w-9 sm:min-w-0"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 ? (
          <span
            data-testid="lms-bell-badge"
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white sm:-right-1 sm:-top-1"
          >
            {bellBadgeText(unread)}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Notifications"
          data-testid="lms-bell-panel"
          className="absolute right-0 z-40 mt-2 max-h-[min(32rem,70vh)] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl shadow-lg sm:w-96"
        >
          <NotificationInbox pageSize={BELL_PAGE_SIZE} onUnreadChange={onUnreadChange} />
        </div>
      ) : null}
    </div>
  );
}

export default NotificationBell;
